import axios, { AxiosInstance } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8085/api';

export interface User {
    _id: string;
    email: string;
    fullName: string;
    role: 'admin' | 'manager' | 'user' | 'viewer';
    department?: string;
    status: 'active' | 'inactive';
    lastActive: Date;
    createdAt: Date;
}

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface AuthTokens {
    accessToken: string;
    refreshToken: string;
}

export interface AuthResponse extends AuthTokens {
    user: User;
}

class AuthService {
    private api: AxiosInstance;
    private refreshPromise: Promise<AuthTokens> | null = null;

    constructor() {
        this.api = axios.create({
            baseURL: API_URL,
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Request interceptor to add auth token
        this.api.interceptors.request.use(
            (config) => {
                const token = this.getAccessToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor to handle token refresh
        this.api.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Don't retry refresh token requests or already retried requests
                if (
                    error.response?.status === 401 &&
                    !originalRequest._retry &&
                    !originalRequest.url?.includes('/auth/refresh')
                ) {
                    originalRequest._retry = true;

                    try {
                        const tokens = await this.refreshAccessToken();
                        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
                        return this.api(originalRequest);
                    } catch (refreshError) {
                        this.logout();
                        window.location.href = '/login';
                        return Promise.reject(refreshError);
                    }
                }

                // If refresh token request itself fails with 401, logout immediately
                if (error.response?.status === 401 && originalRequest.url?.includes('/auth/refresh')) {
                    this.logout();
                    window.location.href = '/login';
                }

                return Promise.reject(error);
            }
        );
    }

    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        const response = await this.api.post<AuthResponse>('/auth/login', credentials);
        const { accessToken, refreshToken, user } = response.data;

        this.setTokens(accessToken, refreshToken);
        this.setUser(user);

        return response.data;
    }

    async logout(): Promise<void> {
        try {
            await this.api.post('/auth/logout');
        } catch (error) {
            // Ignore errors during logout
        } finally {
            this.clearAuth();
        }
    }

    async refreshAccessToken(): Promise<AuthTokens> {
        // Prevent multiple refresh requests
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        this.refreshPromise = this.api
            .post<AuthTokens>('/auth/refresh', { refreshToken })
            .then((response) => {
                const { accessToken, refreshToken: newRefreshToken } = response.data;
                this.setTokens(accessToken, newRefreshToken);
                return response.data;
            })
            .finally(() => {
                this.refreshPromise = null;
            });

        return this.refreshPromise;
    }

    async getCurrentUser(): Promise<User> {
        const response = await this.api.get<User>('/auth/me');
        return response.data;
    }

    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        const response = await this.api.post<AuthTokens>('/auth/change-password', {
            currentPassword,
            newPassword,
            confirmPassword: newPassword
        });

        const { accessToken, refreshToken } = response.data;
        this.setTokens(accessToken, refreshToken);
    }

    // Token management
    getAccessToken(): string | null {
        return localStorage.getItem('accessToken');
    }

    getRefreshToken(): string | null {
        return localStorage.getItem('refreshToken');
    }

    async getOnlyOfficeToken(configInfo: any): Promise<string> {
        const response = await this.api.post<any>('/documents/getOnlyOfficeToken', configInfo);

        const { token } = response.data;
        return token;
    }

    setTokens(accessToken: string, refreshToken: string): void {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
    }

    // User management
    getUser(): User | null {
        const userStr = localStorage.getItem('user');
        if (!userStr) return null;

        try {
            return JSON.parse(userStr);
        } catch (error) {
            return null;
        }
    }

    setUser(user: User): void {
        localStorage.setItem('user', JSON.stringify(user));
    }

    // Auth state management
    isAuthenticated(): boolean {
        return !!this.getAccessToken() && !!this.getUser();
    }

    clearAuth(): void {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
    }

    // Role-based access control
    hasRole(requiredRole: User['role']): boolean {
        const user = this.getUser();
        if (!user) return false;

        const roleHierarchy: Record<User['role'], number> = {
            admin: 4,
            manager: 3,
            user: 2,
            viewer: 1
        };

        return roleHierarchy[user.role] >= roleHierarchy[requiredRole];
    }

    hasPermission(resource: string, action: string): boolean {
        const user = this.getUser();
        if (!user) return false;

        // Admin has all permissions
        if (user.role === 'admin') return true;

        // Define permission matrix
        const permissions: Record<User['role'], Record<string, string[]>> = {
            admin: {
                users: ['create', 'read', 'update', 'delete'],
                machines: ['create', 'read', 'update', 'delete'],
                documents: ['create', 'read', 'update', 'delete', 'manage'],
                roles: ['create', 'read', 'update', 'delete'],
                audit: ['read'],
                settings: ['read', 'update']
            },
            manager: {
                users: ['create', 'read', 'update'],
                machines: ['read', 'update'],
                documents: ['create', 'read', 'update', 'delete'],
                roles: ['read'],
                audit: ['read'],
                settings: ['read']
            },
            user: {
                machines: ['read'],
                documents: ['create', 'read', 'update', 'delete']
            },
            viewer: {
                machines: ['read'],
                documents: ['read']
            }
        };

        const userPermissions = permissions[user.role];
        return userPermissions?.[resource]?.includes(action) || false;
    }

    // API instance getter for other services
    getApiInstance(): AxiosInstance {
        return this.api;
    }

    // Get base API URL
    getApiUrl(): string {
        return API_URL;
    }
}

export default new AuthService();
